import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

/**
 * Storage key and version buster for the persistent cache.
 * Changing CACHE_BUSTER forces eviction of old/incompatible caches automatically.
 */
export const CACHE_STORAGE_KEY = 'DOCTOLIK_QUERY_CACHE_V1';
export const CACHE_BUSTER = 'doctolik-v1.0.0';
export const DEFAULT_MAX_AGE = 1000 * 60 * 60 * 24; // 24 hours

/**
 * In-memory fallback storage adapter for environments without window.localStorage
 * (e.g. unit tests, SSR, or private browsing with disabled storage).
 */
class MemoryStorage {
  constructor() {
    this.store = new Map();
  }
  getItem(key) {
    return this.store.has(key) ? this.store.get(key) : null;
  }
  setItem(key, value) {
    this.store.set(key, String(value));
  }
  removeItem(key) {
    this.store.delete(key);
  }
  clear() {
    this.store.clear();
  }
}

/**
 * Resolve the safest available storage interface
 */
export function getSafeStorage() {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch {
      console.warn('[TanStack Persist] localStorage is inaccessible. Falling back to memory storage.');
      return new MemoryStorage();
    }
  }
  return new MemoryStorage();
}

/**
 * Predicate to determine which queries should be persisted to storage.
 * 
 * Rules:
 * 1. Only persist queries that have successfully resolved (`status === 'success'`).
 * 2. Do not persist queries where `meta.persist === false`.
 * 3. Never persist sensitive or single-use operations marked with `meta.isSensitive`.
 */
export function shouldDehydrateQuery(query) {
  if (query.state.status !== 'success') {
    return false;
  }

  // Explicit opt-out via query meta
  if (query.meta?.persist === false || query.meta?.isSensitive === true) {
    return false;
  }

  return true;
}

/**
 * Create the configured sync storage persister for TanStack Query v5
 */
export function createPersister(storage = getSafeStorage(), key = CACHE_STORAGE_KEY, options = {}) {
  return createSyncStoragePersister({
    storage,
    key,
    throttleTime: options.throttleTime ?? 1000,
    serialize: (data) => JSON.stringify(data),
    deserialize: (data) => JSON.parse(data),
    ...options,
  });
}

/** Default singleton persister instance */
export const defaultPersister = createPersister();

/**
 * Dehydration options for TanStack Query client persistence
 */
export const defaultDehydrateOptions = {
  shouldDehydrateQuery,
  shouldDehydrateMutation: () => false, // Mutations should not be persisted across reloads by default
};

/**
 * Default persist options object passed to PersistQueryClientProvider
 */
export const defaultPersistOptions = {
  persister: defaultPersister,
  maxAge: DEFAULT_MAX_AGE,
  buster: CACHE_BUSTER,
  dehydrateOptions: defaultDehydrateOptions,
};

/**
 * Purge all persisted TanStack Query cache from storage and optionally reset QueryClient
 * 
 * @param {import('@tanstack/react-query').QueryClient} [queryClientInstance] - Optional QueryClient to clear in memory
 * @param {Storage} [storageInstance] - Optional storage to clear
 */
export function clearPersistedCache(queryClientInstance = null, storageInstance = getSafeStorage()) {
  try {
    storageInstance.removeItem(CACHE_STORAGE_KEY);
    if (queryClientInstance) {
      queryClientInstance.clear();
    }
    return true;
  } catch (error) {
    console.error('[TanStack Persist] Failed to clear persisted cache:', error);
    return false;
  }
}

/**
 * Inspect persistent cache stats (size, queries count, timestamp)
 * 
 * @param {Storage} [storageInstance]
 * @returns {{ exists: boolean, bytes: number, formattedSize: string, queryCount: number, timestamp: number|null, buster: string|null }}
 */
export function getPersistedCacheStats(storageInstance = getSafeStorage()) {
  try {
    const raw = storageInstance.getItem(CACHE_STORAGE_KEY);
    if (!raw) {
      return {
        exists: false,
        bytes: 0,
        formattedSize: '0 B',
        queryCount: 0,
        timestamp: null,
        buster: null,
      };
    }

    const bytes = new Blob([raw]).size;
    const parsed = JSON.parse(raw);
    const clientState = parsed.clientState || parsed;
    const queryCount = clientState?.queries?.length || 0;
    const timestamp = parsed.timestamp || null;
    const buster = parsed.buster || null;

    let formattedSize = `${bytes} B`;
    if (bytes >= 1024 * 1024) {
      formattedSize = `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    } else if (bytes >= 1024) {
      formattedSize = `${(bytes / 1024).toFixed(1)} KB`;
    }

    return {
      exists: true,
      bytes,
      formattedSize,
      queryCount,
      timestamp,
      buster,
    };
  } catch (error) {
    console.warn('[TanStack Persist] Could not read cache stats:', error);
    return {
      exists: false,
      bytes: 0,
      formattedSize: 'Error',
      queryCount: 0,
      timestamp: null,
      buster: null,
    };
  }
}
